import {
  type DefaultOptions,
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'

export { clearQueryCacheOnUserChange } from './clearQueryCacheOnUserChange'

/**
 * Per-call metadata read by the QueryCache / MutationCache global
 * onError. Set via `useQuery({ meta: ... })` / `useMutation({ meta: ... })`
 * to opt out of or enrich the Sentry report for that call.
 */
export interface SentryMeta {
  skipSentry?: boolean
  sentryTags?: Record<string, string>
  sentryContext?: Record<string, unknown>
}

/**
 * Minimal shape of Sentry's `captureException` accepted by this module.
 * Both `@sentry/react` and `@sentry/react-native` export functions
 * compatible with this signature.
 */
export type CaptureExceptionLike = (
  error: unknown,
  context?: {
    tags?: Record<string, string>
    extra?: Record<string, unknown>
  }
) => unknown

const BASE_DEFAULT_OPTIONS: DefaultOptions = {
  queries: {
    retry: false,
    staleTime: 60 * 1000, // 1 minute
  },
  mutations: {
    retry: false,
  },
}

export interface CreateQueryClientOptions {
  captureException: CaptureExceptionLike
  /**
   * Per-app overrides merged onto the base defaults. Nested `queries`
   * and `mutations` are merged key-by-key; this lets a caller override
   * a single key (e.g. `staleTime`) without clobbering the rest.
   */
  defaultOptions?: DefaultOptions
}

export function createQueryClient({
  captureException,
  defaultOptions,
}: CreateQueryClientOptions): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query): void => {
        const meta = query.meta as SentryMeta | undefined
        if (meta?.skipSentry) return
        captureException(error, {
          tags: { source: 'tanstack-query', ...meta?.sentryTags },
          extra: { queryKey: query.queryKey, ...meta?.sentryContext },
        })
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation): void => {
        const meta = mutation.meta as SentryMeta | undefined
        if (meta?.skipSentry) return
        captureException(error, {
          tags: { source: 'tanstack-mutation', ...meta?.sentryTags },
          extra: {
            mutationKey: mutation.options.mutationKey,
            ...meta?.sentryContext,
          },
        })
      },
    }),
    defaultOptions: {
      queries: {
        ...BASE_DEFAULT_OPTIONS.queries,
        ...defaultOptions?.queries,
      },
      mutations: {
        ...BASE_DEFAULT_OPTIONS.mutations,
        ...defaultOptions?.mutations,
      },
    },
  })
}
